import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// localStorage key for developer mode override
const DEV_MODE_OVERRIDE_KEY = 'ionoff_dev_mode_override';

/**
 * Check if developer mode override is enabled in localStorage
 */
function getDevModeOverride(): boolean {
  try {
    return localStorage.getItem(DEV_MODE_OVERRIDE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Set developer mode override in localStorage
 */
function setDevModeOverride(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(DEV_MODE_OVERRIDE_KEY, 'true');
    } else {
      localStorage.removeItem(DEV_MODE_OVERRIDE_KEY);
    }
  } catch (e) {
    console.error('Failed to set dev mode override:', e);
  }
}

/**
 * Hook to get PROD_MODE from Firestore in real-time
 * 
 * Reads from: system/config.prodMode
 * 
 * When prodMode is true, the following controls are hidden:
 * - cleanview toggle
 * - +human toggle  
 * - selfeval toggle
 * - TxT toggle
 * - admin toggle
 * 
 * 🥚 Easter Egg: Developer mode override
 * - If localStorage has 'ionoff_dev_mode_override' = 'true', prodMode is forced to false
 * - This allows developers to access dev tools without changing the global setting
 * - Toggle by pressing 'z' 8 times while hovering over the ionoff logo
 * 
 * @returns { prodMode: boolean, loading: boolean, devModeOverride: boolean, toggleDevModeOverride: () => void }
 */
export function useProdMode(): { 
  prodMode: boolean; 
  loading: boolean;
  devModeOverride: boolean;
  toggleDevModeOverride: () => void;
} {
  const [firestoreProdMode, setFirestoreProdMode] = useState<boolean>(false);
  const [devModeOverride, setDevModeOverrideState] = useState<boolean>(getDevModeOverride());
  const [loading, setLoading] = useState<boolean>(true);

  // Toggle developer mode override
  const toggleDevModeOverride = useCallback(() => {
    const newValue = !devModeOverride;
    setDevModeOverride(newValue);
    setDevModeOverrideState(newValue);
    
    if (newValue) {
      console.log('🥚 Developer mode ENABLED (easter egg activated)');
      console.log('   prodMode will be forced to FALSE on this client');
      console.log('   To disable, press z 8 times on the logo again, or clear localStorage');
    } else {
      console.log('🥚 Developer mode DISABLED (back to normal prodMode)');
    }
  }, [devModeOverride]);

  useEffect(() => {
    // Listen to system/config document for real-time updates
    const configRef = doc(db, 'system', 'config');
    
    const unsubscribe = onSnapshot(
      configRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const mode = data?.prodMode === true;
          setFirestoreProdMode(mode);
          console.log('🔧 PROD_MODE loaded from Firestore:', mode);
        } else {
          // Document doesn't exist, default to dev mode (false)
          setFirestoreProdMode(false);
          console.log('⚠️ system/config not found, defaulting PROD_MODE to false');
        }
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error loading PROD_MODE from Firestore:', error);
        setFirestoreProdMode(false); // Default to dev mode on error
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Effective prodMode: false if dev override is enabled, otherwise use Firestore value
  const effectiveProdMode = devModeOverride ? false : firestoreProdMode;

  return { 
    prodMode: effectiveProdMode, 
    loading,
    devModeOverride,
    toggleDevModeOverride,
  };
}

